/**
 * AEO Priorities Service
 *
 * API client for AEO (Answer Engine Optimization) prioritization endpoints.
 * Provides functions to fetch URL, Product, and Category priorities,
 * manage exclusion rules, and trigger recalculations.
 *
 * Part of Bot Category Analytics & AEO Prioritization Engine
 *
 * Date: January 16, 2026
 */

import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// ==========================================
// Types
// ==========================================

export interface ScoreBreakdown {
  botVisits: number;
  aiShare: number;
  categoryImportance: number;
  sitemapPosition: number;
  enrichmentBonus: number;
  variantBoost?: number;
  productCount?: number;
  coverageGap?: number;
  revenue?: number;
}

export interface UrlPriority {
  url: string;
  priorityScore: number;
  scoreBreakdown: ScoreBreakdown;
  metrics: {
    botVisits30d: number;
    aiVisits30d: number;
    sitemapPosition: number | null;
    sitemapPositionPercentile: number | null;
  };
  context: {
    productName: string | null;
    category: string | null;
    subcategory: string | null;
    pageType: string | null;
    isEnriched: boolean;
  };
  exclusion: {
    isExcluded: boolean;
    reason: string | null;
  };
}

export interface ProductPriority {
  productName: string;
  priorityScore: number;
  scoreBreakdown: ScoreBreakdown;
  metrics: {
    urlCount: number;
    totalBotVisits30d: number;
    aiBotVisits30d: number;
    enrichedUrlCount: number;
    enrichmentCoveragePct: number;
  };
  context: {
    category: string | null;
    subcategory: string | null;
    sampleUrls: string[];
  };
}

export interface CategoryPriority {
  category: string;
  subcategory: string | null;
  priorityScore: number;
  scoreBreakdown: ScoreBreakdown;
  metrics: {
    totalProducts: number;
    totalUrls: number;
    enrichedProducts: number;
    enrichedUrls: number;
    enrichmentCoveragePct: number;
    totalBotVisits30d: number;
    aiBotVisits30d: number;
  };
  impact: {
    potentialAiVisibilityIncrease: number;
  };
  topProducts: Array<{
    name: string;
    score: number;
    urls: number;
  }>;
}

export interface PrioritySummary {
  totalUrls: number;
  enrichedUrls: number;
  excludedUrls: number;
  includedUrls: number;
  avgPriorityScore: number;
  topCategoryOpportunity: string | null;
}

export interface UrlPrioritiesResponse {
  priorities: UrlPriority[];
  summary: PrioritySummary;
}

export interface ProductPrioritiesResponse {
  priorities: ProductPriority[];
}

export interface CategoryPrioritiesResponse {
  priorities: CategoryPriority[];
}

export interface CalculationResult {
  success: boolean;
  message: string;
  stats: {
    urls: number;
    products: number;
    categories: number;
    duration_ms: number;
  };
}

export interface ImpactProjection {
  success: boolean;
  level: 'url' | 'product' | 'category';
  topN: number;
  itemsToEnrich: number;
  totalPriorityScore: number;
  projectedImpact: {
    aiVisitsCaptured: number;
    percentageOfAiTraffic: number;
    estimatedVisibilityIncrease: string;
  };
}

export interface ExclusionRule {
  id: string;
  rule_type: 'pattern' | 'exact' | 'extension' | 'prefix';
  rule_value: string;
  reason: string | null;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
}

export interface ManualExclusion {
  id: string;
  url_path: string;
  reason: string | null;
  excluded_at: string;
}

export interface ExclusionStats {
  total_urls: number;
  excluded_urls: number;
  included_urls: number;
  exclusion_rate: number;
}

export interface ExclusionRulesResponse {
  success: boolean;
  systemRules: ExclusionRule[];
  siteRules: ExclusionRule[];
  manualExclusions: ManualExclusion[];
  stats: ExclusionStats | null;
}

export interface ExcludedUrl {
  url_path: string;
  priority_score: number;
  exclusion_reason: string;
  bot_visits_30d: number;
}

export interface AnalysisSettings {
  site_id: string;
  catalog_only_mode: boolean;
  include_category_pages: boolean;
  include_product_pages: boolean;
  include_blog_pages: boolean;
  include_other_pages: boolean;
  min_bot_visits_threshold: number;
  min_priority_score: number;
}

// ==========================================
// API Functions
// ==========================================

const BASE_URL = `${API_URL}/aeo/priorities`;

/**
 * Trigger recalculation of all priority scores for a site
 */
export async function calculatePriorities(siteId: string): Promise<CalculationResult> {
  const response = await authFetch(`${BASE_URL}/${siteId}/calculate`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to calculate priorities' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get top URL priorities
 */
export async function getUrlPriorities(
  siteId: string,
  options: {
    limit?: number;
    offset?: number;
    category?: string;
    excludeEnriched?: boolean;
    minScore?: number;
    includeExcluded?: boolean;
  } = {}
): Promise<UrlPrioritiesResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.category) params.set('category', options.category);
  if (options.excludeEnriched !== undefined) params.set('excludeEnriched', String(options.excludeEnriched));
  if (options.minScore !== undefined) params.set('minScore', String(options.minScore));
  if (options.includeExcluded !== undefined) params.set('includeExcluded', String(options.includeExcluded));

  const url = `${BASE_URL}/${siteId}/urls${params.toString() ? `?${params}` : ''}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch URL priorities');
  }
  return response.json();
}

/**
 * Get top product priorities
 */
export async function getProductPriorities(
  siteId: string,
  options: {
    limit?: number;
    offset?: number;
    category?: string;
    excludeEnriched?: boolean;
  } = {}
): Promise<ProductPrioritiesResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.category) params.set('category', options.category);
  if (options.excludeEnriched !== undefined) params.set('excludeEnriched', String(options.excludeEnriched));

  const url = `${BASE_URL}/${siteId}/products${params.toString() ? `?${params}` : ''}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch product priorities');
  }
  return response.json();
}

/**
 * Get top category priorities
 */
export async function getCategoryPriorities(
  siteId: string,
  options: {
    limit?: number;
    offset?: number;
    excludeEnriched?: boolean;
  } = {}
): Promise<CategoryPrioritiesResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.excludeEnriched !== undefined) params.set('excludeEnriched', String(options.excludeEnriched));

  const url = `${BASE_URL}/${siteId}/categories${params.toString() ? `?${params}` : ''}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch category priorities');
  }
  return response.json();
}

/**
 * Get impact projection for enriching top N items
 */
export async function getImpactProjection(
  siteId: string,
  topN: number = 50,
  level: 'url' | 'product' | 'category' = 'url'
): Promise<ImpactProjection> {
  const response = await authFetch(`${BASE_URL}/${siteId}/impact?topN=${topN}&level=${level}`);
  if (!response.ok) {
    throw new Error('Failed to fetch impact projection');
  }
  return response.json();
}

/**
 * Get priority summary for a site
 */
export async function getPrioritySummary(siteId: string): Promise<{ success: boolean; summary: any }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/summary`);
  if (!response.ok) {
    throw new Error('Failed to fetch priority summary');
  }
  return response.json();
}

/**
 * Export priorities as CSV
 */
export async function exportPriorities(
  siteId: string,
  level: 'url' | 'product' | 'category'
): Promise<Blob> {
  const response = await authFetch(`${BASE_URL}/${siteId}/${level}s/export`);
  if (!response.ok) {
    throw new Error('Failed to export priorities');
  }
  return response.blob();
}

// ==========================================
// Exclusion Management Functions
// ==========================================

/**
 * Get all exclusion rules for a site
 */
export async function getExclusionRules(siteId: string): Promise<ExclusionRulesResponse> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions`);
  if (!response.ok) {
    throw new Error('Failed to fetch exclusion rules');
  }
  return response.json();
}

/**
 * Add a new exclusion rule
 */
export async function addExclusionRule(
  siteId: string,
  rule: { ruleType: string; ruleValue: string; reason?: string }
): Promise<{ success: boolean; rule: ExclusionRule }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rule),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to add rule' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Remove an exclusion rule
 */
export async function removeExclusionRule(
  siteId: string,
  ruleId: string
): Promise<{ success: boolean; deleted: boolean }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/rules/${ruleId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to remove rule' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Toggle a rule's active status
 */
export async function toggleExclusionRule(
  siteId: string,
  ruleId: string,
  isActive: boolean
): Promise<{ success: boolean; id: string; is_active: boolean }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/rules/${ruleId}/toggle`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to toggle rule' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Add a manual URL exclusion
 */
export async function addManualExclusion(
  siteId: string,
  urlPath: string,
  reason?: string
): Promise<{ success: boolean; exclusion: ManualExclusion }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ urlPath, reason }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to add exclusion' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Remove a manual URL exclusion
 */
export async function removeManualExclusion(
  siteId: string,
  urlPath: string
): Promise<{ success: boolean; removed: boolean }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/manual`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ urlPath }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to remove exclusion' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get list of excluded URLs
 */
export async function getExcludedUrls(
  siteId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ success: boolean; urls: ExcludedUrl[]; total: number }> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));

  const url = `${BASE_URL}/${siteId}/exclusions/excluded-urls${params.toString() ? `?${params}` : ''}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch excluded URLs');
  }
  return response.json();
}

/**
 * Preview bulk exclusion by pattern
 */
export async function previewBulkExclusion(
  siteId: string,
  pattern: string,
  ruleType: 'pattern' | 'extension' | 'prefix' = 'pattern'
): Promise<{ success: boolean; preview: ExcludedUrl[]; totalAffected: number }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/exclusions/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern, ruleType }),
  });
  if (!response.ok) {
    throw new Error('Failed to preview exclusion');
  }
  return response.json();
}

// ==========================================
// Analysis Settings Functions
// ==========================================

/**
 * Get analysis settings for a site
 */
export async function getAnalysisSettings(siteId: string): Promise<{ success: boolean; settings: AnalysisSettings }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/settings`);
  if (!response.ok) {
    throw new Error('Failed to fetch settings');
  }
  return response.json();
}

/**
 * Update analysis settings for a site
 */
export async function updateAnalysisSettings(
  siteId: string,
  settings: Partial<AnalysisSettings>
): Promise<{ success: boolean; settings: AnalysisSettings }> {
  const response = await authFetch(`${BASE_URL}/${siteId}/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error('Failed to update settings');
  }
  return response.json();
}

/**
 * Download CSV file
 */
export function downloadCsv(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ==========================================
// Sitemap Configuration Functions
// ==========================================

export interface SitemapTestResult {
  success: boolean;
  status: 'success' | 'partial' | 'failed';
  sitemapUrl: string | null;
  urlsFound: number;
  errors: Array<{ type: string; message: string; url?: string }>;
  durationMs: number;
  httpStatus: number | null;
  contentType: string | null;
}

export interface SitemapUrlConfig {
  sitemapUrl: string | null;
  defaultUrl: string | null;
  isConfigured: boolean;
}

/**
 * Test a sitemap URL (with optional custom URL)
 */
export async function testSitemap(
  siteId: string,
  customUrl?: string
): Promise<SitemapTestResult> {
  const response = await authFetch(`${API_URL}/robot-diagnostics/sitemap/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, url: customUrl }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to test sitemap' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Save sitemap URL for a site
 */
export async function saveSitemapUrl(
  siteId: string,
  sitemapUrl: string
): Promise<{ success: boolean; sitemapUrl: string }> {
  const response = await authFetch(`${API_URL}/sites/${siteId}/sitemap-url`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sitemapUrl }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to save sitemap URL' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get sitemap URL configuration for a site
 */
export async function getSitemapUrlConfig(siteId: string): Promise<SitemapUrlConfig> {
  const response = await authFetch(`${API_URL}/sites/${siteId}/sitemap-url`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get sitemap URL' }));
    throw new Error(error.message);
  }
  return response.json();
}
